import { CheckCircle2, X, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import QrScannerView from "../QrScannerView/QrScannerView";
import CfLoader from "../../common/CfLoader/CfLoader";
import {
  buildVcVerificationDisplayData,
  formatVerificationCreatedAt,
  getDisplayCredentialId,
} from "../../utils/vcVerificationDisplay";
import {
  getPublicVerificationStatusLabel,
  getPublicVerificationStatusMessage,
  normalizePublicVerificationStatusCode,
} from "../../utils/publicVerificationStatus";

const formatLabel = (label) =>
  label
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const renderValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(renderValue).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const PublicVerificationDialog = ({
  isLoading,
  mode,
  onClose,
  onCredentialSubmit,
  onScan,
  onScanError,
  result,
}) => {
  const { t } = useTranslation(["publicPortal", "verify"]);
  const [credentialId, setCredentialId] = useState("");
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const statusCode = result
    ? normalizePublicVerificationStatusCode(result)
    : null;
  const displayStatus = result
    ? getPublicVerificationStatusLabel(result, t)
    : null;
  const statusMessage = result
    ? getPublicVerificationStatusMessage(result, t)
    : null;
  const displayData = result
    ? buildVcVerificationDisplayData(
        result,
        t("publicPortal:verification.notAvailable"),
      )
    : {};
  const displayCredentialId = result ? getDisplayCredentialId(result) : null;
  const verifiedAt = result
    ? formatVerificationCreatedAt(result.issued_at || result.created_at)
    : null;
  const isUnsuccessful = statusCode !== null && statusCode !== "VERIFIED";

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    closeButtonRef.current?.focus();

    return () => {
      const previous = previouslyFocusedRef.current;
      if (previous && typeof previous.focus === "function") {
        previous.focus();
      }
    };
  }, []);

  const handleDialogKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose?.();
      return;
    }

    if (event.key !== "Tab" || !dialogRef.current) return;

    const focusable = dialogRef.current.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const value = credentialId.trim();
    if (value) onCredentialSubmit(value);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-publicPortal-overlay p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={t("verification.dialogTitle")}
      ref={dialogRef}
      onKeyDown={handleDialogKeyDown}
    >
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-cfSecondary p-5 shadow-xl sm:p-7">
        <button
          type="button"
          ref={closeButtonRef}
          className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-cfSearchContainer text-cfGlobalText transition hover:text-cfHeadingText"
          onClick={onClose}
          aria-label={t("verification.close")}
        >
          <X aria-hidden="true" size={20} />
        </button>

        {isLoading && (
          <div className="flex min-h-[320px] items-center justify-center">
            <CfLoader />
          </div>
        )}

        {!isLoading && result && (
          <div className="px-2 py-8 text-center sm:px-8">
            {isUnsuccessful ? (
              <XCircle
                className="mx-auto mb-5 text-cfStatusBlockedText"
                size={64}
              />
            ) : (
              <CheckCircle2 className="mx-auto mb-5 text-cfPrimary" size={64} />
            )}
            <h2 className="mb-3 text-2xl font-bold text-cfHeadingText">
              {isUnsuccessful
                ? t("verification.unsuccessful")
                : t("verification.successful")}
            </h2>
            {displayStatus && (
              <p className="mb-5 font-semibold text-cfCardTagText">
                {displayStatus}
              </p>
            )}
            <div className="mx-auto max-w-xl rounded-xl bg-cfSearchContainer p-5 text-left text-sm text-cfGlobalText">
              {statusMessage && <p className="m-0">{statusMessage}</p>}
              <dl className="m-0 grid gap-x-6 gap-y-4 sm:grid-cols-2">
                {displayCredentialId && (
                  <div>
                    <dt className="font-semibold text-cfCardTagText">
                      {t("publicPortal:verification.credentialId")}
                    </dt>
                    <dd className="m-0 mt-1 break-words">
                      {displayCredentialId}
                    </dd>
                  </div>
                )}
                {verifiedAt && (
                  <div>
                    <dt className="font-semibold text-cfCardTagText">
                      {t("publicPortal:verification.verifiedAt")}
                    </dt>
                    <dd className="m-0 mt-1">{verifiedAt}</dd>
                  </div>
                )}
                {Object.entries(displayData)
                  .filter(([label]) => !label.startsWith("__"))
                  .map(([label, value]) => (
                    <div key={label}>
                      <dt className="font-semibold text-cfCardTagText">
                        {t(`verify:${label}`, {
                          defaultValue: formatLabel(label),
                        })}
                      </dt>
                      <dd className="m-0 mt-1 break-words">
                        {renderValue(value)}
                      </dd>
                    </div>
                  ))}
              </dl>
            </div>
          </div>
        )}

        {!isLoading && !result && mode === "scan" && (
          <div>
            <h2 className="mb-5 pr-12 text-xl font-bold text-cfHeadingText">
              {t("publicPortal:methods.scan.title")}
            </h2>
            <div className="relative mx-auto aspect-square w-full max-w-md overflow-hidden rounded-lg bg-black">
              <div className="absolute inset-0">
                <QrScannerView
                  onScan={onScan}
                  onError={onScanError}
                  hintText={t("publicPortal:verification.scanHint")}
                />
              </div>
            </div>
          </div>
        )}

        {!isLoading && !result && mode === "credential" && (
          <form className="py-5" onSubmit={handleSubmit}>
            <h2 className="mb-3 pr-12 text-xl font-bold text-cfHeadingText">
              {t("publicPortal:methods.credential.title")}
            </h2>
            <p className="mb-5 text-sm text-cfGlobalText">
              {t("publicPortal:methods.credential.description")}
            </p>
            <label
              className="mb-2 block text-sm font-semibold text-cfCardTagText"
              htmlFor="public-credential-id"
            >
              {t("publicPortal:methods.credential.title")}
            </label>
            <input
              id="public-credential-id"
              className="mb-5 w-full rounded-[8px] border border-cfCardBorder bg-cfSecondary px-4 py-3 text-cfHeadingText outline-none focus:border-cfPrimary focus:ring-2 focus:ring-cfPrimaryLight"
              value={credentialId}
              onChange={(event) => setCredentialId(event.target.value)}
              placeholder={t("verification.credentialPlaceholder")}
              autoFocus
            />
            <button
              type="submit"
              disabled={!credentialId.trim()}
              className="w-full rounded-[8px] bg-cfPrimary px-5 py-3 font-semibold text-publicPortal-onBrand transition hover:bg-cfPrimaryDark disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("verification.verify")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PublicVerificationDialog;
